import { defineStore } from "pinia";
import { ref, computed } from "vue";

// Claves lógicas de cada step que usan los setters y el mapeo de errores.
// Mantenerlas alineadas con los `name` de stepsConfig.js (sin el prefijo newActuacion-).
export const WIZARD_STEP_KEYS = [
  "afectados",
  "vinculados",
  "fecha",
  "efectos",
  "intervinientes",
  "afectaciones",
  "datosLegales",
  "visualizacion",
];

const emptyStepErrors = () =>
  WIZARD_STEP_KEYS.reduce((acc, k) => {
    acc[k] = [];
    return acc;
  }, {});

export const useActuacionWizardStore = defineStore("actuacionWizard", () => {
  // state
  const type = ref(null);
  const datosLegales = ref({ data: {}, valid: false });
  const afectados = ref({ data: {}, valid: false });
  const vinculados = ref({ data: {}, valid: false });
  const fecha = ref({ data: {}, valid: false });
  const intervinientes = ref({ data: {}, valid: false });
  const efectos = ref({ data: {}, valid: false });
  const afectaciones = ref({ data: {}, valid: false });
  const relato = ref("");
  const actaDoc = ref(null);
  const actaSourceSignature = ref("");

  // submit state
  const submitting = ref(false);
  const submitError = ref(null);
  const createdActuacionId = ref(null);
  // errores del backend mapeados al step correspondiente para pintarlo en rojo
  const stepErrors = ref(emptyStepErrors());

  // actions
  const setType = (t) => {
    type.value = t || null;
  };

  const setDatosLegalesData = (payload) => {
    datosLegales.value = { ...datosLegales.value, data: payload || {} };
  };

  const setDatosLegalesValid = (flag) => {
    datosLegales.value = { ...datosLegales.value, valid: !!flag };
  };

  const setAfectadosData = (payload) => {
    afectados.value = { ...afectados.value, data: payload || {} };
  };

  const setAfectadosValid = (flag) => {
    afectados.value = { ...afectados.value, valid: !!flag };
  };

  const setVinculadosData = (payload) => {
    vinculados.value = { ...vinculados.value, data: payload || {} };
  };

  const setVinculadosValid = (flag) => {
    vinculados.value = { ...vinculados.value, valid: !!flag };
  };

  const setIntervinientesData = (payload) => {
    intervinientes.value = { ...intervinientes.value, data: payload || {} };
  };

  const setIntervinientesValid = (flag) => {
    intervinientes.value = { ...intervinientes.value, valid: !!flag };
  };

  const setEfectosData = (payload) => {
    efectos.value = { ...efectos.value, data: payload || {} };
  };

  const setEfectosValid = (flag) => {
    efectos.value = { ...efectos.value, valid: !!flag };
  };

  const setAfectacionesData = (payload) => {
    afectaciones.value = { ...afectaciones.value, data: payload || {} };
  };

  const setAfectacionesValid = (flag) => {
    afectaciones.value = { ...afectaciones.value, valid: !!flag };
  };

  const setStepData = (stepKey, payload) => {
    if (stepKey === "afectados") afectados.value = { ...afectados.value, data: payload || {} };
    if (stepKey === "vinculados") vinculados.value = { ...vinculados.value, data: payload || {} };
    if (stepKey === "fecha") fecha.value = { ...fecha.value, data: payload || {} };
    if (stepKey === "intervinientes")
      intervinientes.value = { ...intervinientes.value, data: payload || {} };
    if (stepKey === "efectos") efectos.value = { ...efectos.value, data: payload || {} };
    if (stepKey === "afectaciones")
      afectaciones.value = { ...afectaciones.value, data: payload || {} };
    if (stepKey === "datosLegales")
      datosLegales.value = { ...datosLegales.value, data: payload || {} };
  };

  const setStepValid = (stepKey, flag) => {
    if (stepKey === "afectados") afectados.value = { ...afectados.value, valid: !!flag };
    if (stepKey === "vinculados") vinculados.value = { ...vinculados.value, valid: !!flag };
    if (stepKey === "fecha") fecha.value = { ...fecha.value, valid: !!flag };
    if (stepKey === "intervinientes")
      intervinientes.value = { ...intervinientes.value, valid: !!flag };
    if (stepKey === "efectos") efectos.value = { ...efectos.value, valid: !!flag };
    if (stepKey === "afectaciones") afectaciones.value = { ...afectaciones.value, valid: !!flag };
    if (stepKey === "datosLegales")
      datosLegales.value = { ...datosLegales.value, valid: !!flag };
  };

  const setRelato = (text) => {
    relato.value = typeof text === "string" ? text : "";
  };

  const setActaDoc = (payload, signature = "") => {
    actaDoc.value = payload || null;
    actaSourceSignature.value = signature || "";
  };

  // --- manejo de errores de backend por step ---
  const clearStepErrors = () => {
    stepErrors.value = emptyStepErrors();
    submitError.value = null;
  };

  const setStepErrors = (mapByStep) => {
    // mapByStep: { [stepKey]: string[] }
    const next = emptyStepErrors();
    Object.entries(mapByStep || {}).forEach(([k, v]) => {
      if (next[k] !== undefined && Array.isArray(v)) next[k] = v.slice();
    });
    stepErrors.value = next;
  };

  const pushStepError = (stepKey, message) => {
    if (!stepKey || !message) return;
    const current = stepErrors.value[stepKey] || [];
    stepErrors.value = {
      ...stepErrors.value,
      [stepKey]: [...current, message],
    };
  };

  const setSubmitting = (flag) => {
    submitting.value = !!flag;
  };

  const setSubmitError = (err) => {
    submitError.value = err || null;
  };

  const setCreatedActuacionId = (id) => {
    createdActuacionId.value = id || null;
  };

  const hasNonEmpty = (val) => {
    if (Array.isArray(val)) return val.length > 0;
    if (val && typeof val === "object") return Object.values(val).some(hasNonEmpty);
    return val !== undefined && val !== null && val !== "";
  };

  const isDirty = computed(() => {
    return (
      hasNonEmpty(datosLegales.value?.data) ||
      hasNonEmpty(afectados.value?.data) ||
      hasNonEmpty(vinculados.value?.data) ||
      hasNonEmpty(fecha.value?.data) ||
      hasNonEmpty(efectos.value?.data) ||
      hasNonEmpty(intervinientes.value?.data) ||
      hasNonEmpty(afectaciones.value?.data)
    );
  });

  const stepHasError = (stepKey) =>
    Array.isArray(stepErrors.value[stepKey]) && stepErrors.value[stepKey].length > 0;

  const reset = () => {
    type.value = null;
    datosLegales.value = { data: {}, valid: false };
    afectados.value = { data: {}, valid: false };
    vinculados.value = { data: {}, valid: false };
    fecha.value = { data: {}, valid: false };
    intervinientes.value = { data: {}, valid: false };
    efectos.value = { data: {}, valid: false };
    afectaciones.value = { data: {}, valid: false };
    relato.value = "";
    actaDoc.value = null;
    actaSourceSignature.value = "";
    submitting.value = false;
    submitError.value = null;
    createdActuacionId.value = null;
    stepErrors.value = emptyStepErrors();
  };

  return {
    // state
    type,
    relato,
    datosLegales,
    afectados,
    vinculados,
    fecha,
    intervinientes,
    efectos,
    afectaciones,
    actaDoc,
    actaSourceSignature,
    submitting,
    submitError,
    createdActuacionId,
    stepErrors,

    // actions
    setType,
    setRelato,
    setDatosLegalesData,
    setDatosLegalesValid,
    setAfectadosData,
    setAfectadosValid,
    setVinculadosData,
    setVinculadosValid,
    setIntervinientesData,
    setIntervinientesValid,
    setEfectosData,
    setEfectosValid,
    setAfectacionesData,
    setAfectacionesValid,
    setStepData,
    setStepValid,
    setActaDoc,
    clearStepErrors,
    setStepErrors,
    pushStepError,
    setSubmitting,
    setSubmitError,
    setCreatedActuacionId,
    reset,

    // computed / helpers
    isDirty,
    stepHasError,
  };
});
